/**
 * onePagerSlice.ts
 * Redux Toolkit slice for KPI One Pager V2 state management.
 * Manages grain configuration, table sort, and UI state.
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	GrainKey,
	TableSortState,
	DEFAULT_GRAIN_SELECTIONS,
	DEFAULT_SORT,
	MAX_GRAIN_COLUMNS,
} from '../../screens/AnalyticsHub/constants/onePagerConstants';

// ─── State Interface ──────────────────────────────────────────────────────────

export interface OnePagerState {
	/** Working copy of grain selections (edited in config panel, not yet applied) */
	pendingGrains: string[];
	/** Committed grain selections that drive the table rendering */
	grainSelections: GrainKey[];
	/** Number of visible dropdown slots in the config panel */
	grainSlotCount: number;
	/** Whether user has manually applied a grain config (suppresses auto drill-down) */
	manualOverride: boolean;
	/** Current table sort state */
	sort: TableSortState;
	/** Set of expanded row keys (for future hierarchical expansion) */
	expandedRows: string[];
	/** Whether the Chart Configuration panel is expanded */
	isConfigExpanded: boolean;
}

// ─── Initial State ────────────────────────────────────────────────────────────

const initialState: OnePagerState = {
	pendingGrains: [...DEFAULT_GRAIN_SELECTIONS],
	grainSelections: [...DEFAULT_GRAIN_SELECTIONS],
	grainSlotCount: 1,
	manualOverride: false,
	sort: { ...DEFAULT_SORT },
	expandedRows: [],
	isConfigExpanded: false,
};

// ─── Slice ────────────────────────────────────────────────────────────────────

const onePagerSlice = createSlice({
	name: 'onePager',
	initialState,
	reducers: {
		/** Toggle the Chart Configuration panel open/closed */
		toggleConfigPanel(state) {
			state.isConfigExpanded = !state.isConfigExpanded;
		},

		/** Set the config panel expanded state explicitly */
		setConfigExpanded(state, action: PayloadAction<boolean>) {
			state.isConfigExpanded = action.payload;
		},

		/** Update a pending grain selection at a specific slot index */
		setPendingGrain(state, action: PayloadAction<{ index: number; value: string }>) {
			const { index, value } = action.payload;
			// Clear any other slot that has this value (enforce uniqueness)
			if (value) {
				state.pendingGrains = state.pendingGrains.map((v) => (v === value ? '' : v));
			}
			// Ensure array is large enough
			while (state.pendingGrains.length <= index) {
				state.pendingGrains.push('');
			}
			state.pendingGrains[index] = value;
		},

		/** Add a new empty grain slot */
		addGrainSlot(state) {
			if (state.grainSlotCount < MAX_GRAIN_COLUMNS) {
				state.grainSlotCount++;
				while (state.pendingGrains.length < state.grainSlotCount) {
					state.pendingGrains.push('');
				}
			}
		},

		/** Remove a grain slot at a specific index */
		removeGrainSlot(state, action: PayloadAction<number>) {
			if (state.grainSlotCount <= 1) return;
			const index = action.payload;
			state.pendingGrains.splice(index, 1);
			state.grainSlotCount--;
			// Ensure pending array matches slot count
			while (state.pendingGrains.length < state.grainSlotCount) {
				state.pendingGrains.push('');
			}
		},

		/** Apply pending grains to committed state (user clicked "Apply") */
		applyGrainConfig(state) {
			const committed = state.pendingGrains.filter((v) => v !== '') as GrainKey[];
			if (committed.length === 0) return; // Must have at least one
			state.grainSelections = committed;
			state.pendingGrains = [...committed];
			state.grainSlotCount = Math.max(state.grainSlotCount, committed.length);
			state.manualOverride = true;
		},

		/** Reset grain config to auto drill-down mode */
		resetGrainConfig(state) {
			state.manualOverride = false;
			// Auto drill will be computed externally and dispatched via setAutoDrillGrains
		},

		/** Set grains from auto drill-down logic (called when manualOverride is false) */
		setAutoDrillGrains(state, action: PayloadAction<{ selections: GrainKey[]; slotCount: number }>) {
			if (state.manualOverride) return; // Don't override manual config
			const { selections, slotCount } = action.payload;
			state.grainSelections = selections;
			state.pendingGrains = [...selections];
			state.grainSlotCount = slotCount;
		},

		/** Restore grain config from a saved view */
		restoreGrainConfig(
			state,
			action: PayloadAction<{ selections: GrainKey[]; slotCount: number; manual: boolean }>
		) {
			const { selections, slotCount, manual } = action.payload;
			state.grainSelections = selections;
			state.pendingGrains = [...selections];
			state.grainSlotCount = slotCount;
			state.manualOverride = manual;
		},

		/** Update table sort state */
		setSort(state, action: PayloadAction<string>) {
			const col = action.payload;
			if (state.sort.col === col) {
				// Toggle direction, then reset
				if (state.sort.dir === 'asc') {
					state.sort = { col, dir: 'desc' };
				} else {
					state.sort = { ...DEFAULT_SORT };
				}
			} else {
				state.sort = { col, dir: 'asc' };
			}
		},

		/** Toggle a row's expanded state */
		toggleRowExpanded(state, action: PayloadAction<string>) {
			const key = action.payload;
			const index = state.expandedRows.indexOf(key);
			if (index >= 0) {
				state.expandedRows.splice(index, 1);
			} else {
				state.expandedRows.push(key);
			}
		},

		/** Reset all One Pager state to initial */
		resetOnePagerState() {
			return { ...initialState };
		},
	},
});

// ─── Exports ──────────────────────────────────────────────────────────────────

export const {
	toggleConfigPanel,
	setConfigExpanded,
	setPendingGrain,
	addGrainSlot,
	removeGrainSlot,
	applyGrainConfig,
	resetGrainConfig,
	setAutoDrillGrains,
	restoreGrainConfig,
	setSort,
	toggleRowExpanded,
	resetOnePagerState,
} = onePagerSlice.actions;

export default onePagerSlice.reducer;

// ─── Selectors ────────────────────────────────────────────────────────────────

type RootStateWithOnePager = { onePager: OnePagerState };

export const selectGrainSelections = (state: RootStateWithOnePager) => state.onePager.grainSelections;
export const selectPendingGrains = (state: RootStateWithOnePager) => state.onePager.pendingGrains;
export const selectGrainSlotCount = (state: RootStateWithOnePager) => state.onePager.grainSlotCount;
export const selectManualOverride = (state: RootStateWithOnePager) => state.onePager.manualOverride;
export const selectSort = (state: RootStateWithOnePager) => state.onePager.sort;
export const selectIsConfigExpanded = (state: RootStateWithOnePager) => state.onePager.isConfigExpanded;
export const selectExpandedRows = (state: RootStateWithOnePager) => state.onePager.expandedRows;

/** Check if there are unapplied pending changes */
export const selectHasPendingChanges = (state: RootStateWithOnePager): boolean => {
	const { pendingGrains, grainSelections } = state.onePager;
	const committed = pendingGrains.filter((v) => v !== '');
	if (committed.length !== grainSelections.length) return true;
	for (let i = 0; i < committed.length; i++) {
		if (committed[i] !== grainSelections[i]) return true;
	}
	return false;
};
